import type { ComponentProps, ComponentType } from 'react'

import { cn } from '@/lib/utils'

export type StyledIconTone =
  | 'success'
  | 'warning'
  | 'info'
  | 'destructive'
  | 'brand'
  | 'amethyst'
  | 'periwinkle'
  | 'mint'
  | 'neutral'

const iconToneVariants: Record<StyledIconTone, { background: string; icon: string }> = {
  success: {
    background: 'bg-status-success-background',
    icon: 'text-status-success-foreground',
  },
  warning: {
    background: 'bg-status-warning-background',
    icon: 'text-status-warning-foreground',
  },
  info: {
    background: 'bg-status-info-background',
    icon: 'text-status-info-foreground',
  },
  destructive: {
    background: 'bg-status-destructive-background',
    icon: 'text-status-destructive-foreground',
  },
  brand: {
    background: 'bg-accentSecondary',
    icon: 'text-interaction',
  },
  amethyst: { background: 'bg-accentSecondary', icon: 'text-interaction' },
  periwinkle: { background: 'bg-accent', icon: 'text-status-info-foreground' },
  mint: { background: 'bg-brand-mint/70 dark:bg-chart-3/15', icon: 'text-chart-3' },
  neutral: { background: 'bg-muted', icon: 'text-muted-foreground' },
}

export type StyledIconProps = ComponentProps<'div'> & {
  icon: ComponentType<{ className?: string }>
  tone?: StyledIconTone
  iconClassName?: string
}

function StyledIcon({
  icon: Icon,
  tone = 'neutral',
  className,
  iconClassName,
  ...props
}: StyledIconProps) {
  const variant = iconToneVariants[tone] || iconToneVariants.neutral

  return (
    <div
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-md',
        variant.background,
        className
      )}
      data-tone={tone}
      {...props}
    >
      <Icon className={cn('size-5 shrink-0', variant.icon, iconClassName)} />
    </div>
  )
}

export { StyledIcon }
